import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function FelpeArticle() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Itaobuy Spreadsheet Felpe: Hoodie, Maglioni e Streetwear con Foto QC',
    description: 'Scopri le migliori felpe e hoodie su Itaobuy Spreadsheet. Guida italiana con foto QC, prezzi e consigli per trovare streetwear di qualità nella categoria felpe e maglioni.',
    author: { '@type': 'Organization', name: 'Itaobuy Spread' },
    datePublished: '2026-07-14',
    inLanguage: 'it',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Che tipo di felpe trovo su Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Hoodie con cappuccio, crewneck, maglioni in cotone e lana, felpe oversize e modelli streetwear. Ogni prodotto include foto QC dettagliate.' } },
      { '@type': 'Question', name: 'Come valuto la qualità di una felpa dalle foto QC?', acceptedAnswer: { '@type': 'Answer', text: 'Controlla il peso del tessuto nelle foto ravvicinate, la qualità delle cuciture su polsi e collo, la stampa o il ricamo e la consistenza generale del materiale.' } },
      { '@type': 'Question', name: 'Itaobuy Spreadsheet aggiorna le felpe regolarmente?', acceptedAnswer: { '@type': 'Answer', text: 'Sì, la categoria felpe viene aggiornata con nuovi modelli, nuove collezioni e prodotti stagionali per offrirti sempre le migliori opzioni disponibili.' } },
    ],
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.mom';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Itaobuy Spreadsheet Felpe', item: `${siteUrl}/itaobuy-spreadsheet-felpe` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-20">
        <article className="px-6 md:px-10 max-w-4xl mx-auto py-10 md:py-16">
          <nav className="text-xs text-warm-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-tealwarm-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-warm-600">Itaobuy Spreadsheet Felpe</span>
          </nav>

          <header>
            <span className="inline-block px-3 py-1 rounded-full bg-tealwarm-100 text-tealwarm-700 text-xs font-semibold uppercase tracking-wide mb-4">
              Categoria Felpe
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-warm-950 leading-tight">
              <span className="text-tealwarm-600">Itaobuy Spreadsheet Felpe:</span> Hoodie, Maglioni e Streetwear di Qualità
            </h1>
            <p className="mt-4 text-sm md:text-base text-warm-600 leading-relaxed">
              Le felpe e i maglioni sono tra i capi più cercati su <strong>itaobuy spreadsheet felpe</strong>. Che tu stia cercando un hoodie oversize per un look streetwear o un maglione elegante in cotone pesante, questa categoria raccoglie il meglio con foto QC dettagliate che ti mostrano materiali, cuciture e vestibilità reali.
            </p>
          </header>

          <div className="mt-10">
            <img
              src="https://readdy.ai/api/search-image?query=Carefully%20styled%20flat%20lay%20of%20three%20premium%20hoodies%20and%20crewneck%20sweaters%20in%20oatmeal%20sage%20green%20and%20charcoal%20tones%20arranged%20on%20a%20textured%20wool%20blanket%20with%20dried%20autumn%20leaves%20scattered%20around%2C%20soft%20diffused%20window%20light%20creating%20gentle%20shadows%2C%20cozy%20autumn%20fashion%20editorial%20aesthetic%2C%20warm%20earthy%20atmosphere&width=1200&height=600&seq=article-felpe-hero&orientation=landscape"
              alt="Itaobuy Spreadsheet Felpe hoodie"
              title="Itaobuy Spreadsheet Felpe"
              className="w-full rounded-2xl"
            />
          </div>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              La guida definitiva alle <span className="text-tealwarm-600">felpe su Itaobuy Spreadsheet</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-warm-700 leading-relaxed">
              L&apos;<strong>itaobuy spreadsheet</strong> ha rivoluzionato il modo di cercare felpe online. Invece di navigare tra migliaia di risultati senza filtro, accedi a una selezione curata di hoodie, crewneck e maglioni con foto QC che mostrano esattamente ciò che riceverai. I dettagli fanno la differenza: il peso del tessuto, la qualità del ricamo, la consistenza del pile interno — tutto è visibile nelle immagini QC incluse nello spreadsheet.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Come scegliere la <span className="text-tealwarm-600">felpa perfetta</span>
            </h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-cream-50 rounded-2xl p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 mx-auto mb-3">
                  <i className="ri-scales-3-line text-xl"></i>
                </div>
                <h3 className="font-bold text-warm-900">Peso Tessuto</h3>
                <p className="mt-2 text-sm text-warm-600 leading-relaxed">Controlla il grammage nelle foto QC. Tessuti più pesanti offrono migliore struttura e durata nel tempo.</p>
              </div>
              <div className="bg-cream-50 rounded-2xl p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 mx-auto mb-3">
                  <i className="ri-ruler-line text-xl"></i>
                </div>
                <h3 className="font-bold text-warm-900">Vestibilità</h3>
                <p className="mt-2 text-sm text-warm-600 leading-relaxed">Osserva le proporzioni nelle foto: lunghezza maniche, ampiezza torace e caduta del tessuto.</p>
              </div>
              <div className="bg-cream-50 rounded-2xl p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 mx-auto mb-3">
                  <i className="ri-brush-line text-xl"></i>
                </div>
                <h3 className="font-bold text-warm-900">Stampe e Ricami</h3>
                <p className="mt-2 text-sm text-warm-600 leading-relaxed">Verifica la nitidezza di stampe e ricami: bordi puliti e colori fedeli sono segno di qualità.</p>
              </div>
            </div>
          </section>

          <section className="mt-12">
            <div className="bg-warm-50 rounded-2xl p-6 md:p-8">
              <h3 className="font-display text-xl md:text-2xl font-bold text-warm-950">Lo sapevi?</h3>
              <p className="mt-3 text-sm md:text-base text-warm-700 leading-relaxed">
                La categoria <strong>itaobuy spreadsheet felpe</strong> è una delle più aggiornate: ogni settimana vengono aggiunti nuovi modelli dalle ultime collezioni streetwear. Se cerchi hoodie con cappuccio, crewneck basic o maglioni invernali pesanti, troverai sempre qualcosa di nuovo da scoprire. Le foto QC ti permettono di vedere il retro, i dettagli del cappuccio e le etichette interne.
              </p>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">Domande frequenti</h2>
            <div className="mt-5 space-y-4">
              {[
                { q: 'Che tipo di felpe trovo su Itaobuy Spreadsheet?', a: 'Hoodie con cappuccio, crewneck, maglioni in cotone e lana, felpe oversize e modelli streetwear. Ogni prodotto include foto QC dettagliate.' },
                { q: 'Come valuto la qualità di una felpa dalle foto QC?', a: 'Controlla il peso del tessuto, la qualità delle cuciture su polsi e collo, la stampa o il ricamo e la consistenza generale del materiale nelle foto ravvicinate.' },
                { q: 'Itaobuy Spreadsheet aggiorna le felpe regolarmente?', a: 'Sì, la categoria viene aggiornata con nuovi modelli, collezioni e prodotti stagionali per offrirti sempre le migliori opzioni.' },
              ].map((item, i) => (
                <div key={i} className="bg-cream-50 rounded-2xl p-5">
                  <h3 className="font-bold text-warm-900">{item.q}</h3>
                  <p className="mt-2 text-sm text-warm-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 bg-gradient-to-r from-tealwarm-50 to-cream-50 rounded-2xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-warm-950">
              Scopri le <span className="text-tealwarm-600">migliori felpe</span> ora
            </h2>
            <p className="mt-3 text-sm text-warm-600 max-w-md mx-auto">
              Esplora la categoria felpe con foto QC e link diretti ai prodotti selezionati.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-warm-900 text-white text-sm font-semibold hover:bg-warm-800 transition-all whitespace-nowrap cursor-pointer">
                Felpe su Itaobuy Sheet <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-warm-300 text-warm-800 text-sm font-semibold hover:bg-warm-50 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line"></i> Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}